// components/Login/PasswordlessEmailLogin.jsx
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Button, TextField, Typography } from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN; // e.g. dev-xxx.us.auth0.com
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;
const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

// helper: decode JWT payload (no signature verification, just to read claims)
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to parse JWT:", e);
    return null;
  }
};

const MotionBox = motion(Box);

// shared modern input styles
const commonInputRootSx = {
  borderRadius: 3.5, // squarish
  backgroundColor: "rgba(15,23,42,0.85)",
  border: "1px solid rgba(148,163,184,0.5)",
  transition:
    "border-color 0.18s ease-out, box-shadow 0.18s ease-out, background-color 0.18s ease-out",
  "&:hover": {
    borderColor: "rgba(129,140,248,0.9)",
    backgroundColor: "rgba(15,23,42,0.95)",
  },
  "&.Mui-focused": {
    borderColor: "rgba(129,140,248,1)",
    boxShadow: "0 0 0 1px rgba(129,140,248,0.7)",
    backgroundColor: "rgba(15,23,42,0.98)",
  },
};

const commonLabelSx = {
  fontSize: "0.78rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "rgba(148,163,184,0.9)",

  "&.Mui-focused": {
    color: "#E5E7EB",
  },
  "&.MuiInputLabel-shrink": {
    color: "#E5E7EB",
  },
  "&.MuiInputLabel-shrink.Mui-focused": {
    backgroundColor: "rgba(15,23,42,0.98)",
    padding: "0 6px",
    borderRadius: 999,
  },
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PasswordlessEmailLogin = ({ selectedCollege, onSuccess }) => {
  const [step, setStep] = useState("email"); // "email" | "code"

  // form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  // flow flags
  const [isExistingUser, setIsExistingUser] = useState(null); // null = unknown, true/false once checked
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  // e.g. "student@alasu.edu"
  const exampleEmail = useMemo(() => {
    const domain = selectedCollege?.domains?.[0];
    if (!domain) return "you@school.edu";
    const cleanDomain = domain.toLowerCase();
    return `student@${cleanDomain}`;
  }, [selectedCollege]);

  const collegeName = selectedCollege?.name || "your school";

  const collegeId =
    localStorage.getItem("selectedSchoolId") || selectedCollege?.id;

  // normalize + clean email as the user types
  const handleEmailChange = (e) => {
    const raw = e.target.value || "";
    const cleaned = raw.replace(/\s+/g, "").toLowerCase();
    setEmail(cleaned);
    setIsExistingUser(null); // changing email resets flag
  };

  const sendPasswordlessCode = async (cleanEmail) => {
    await axios.post(`https://${AUTH0_DOMAIN}/passwordless/start`, {
      client_id: AUTH0_CLIENT_ID,
      connection: "email",
      email: cleanEmail,
      send: "code",
    });
  };

  /**
   * First click on email step.
   *  - Calls /post-login with checkOnly=true, no auth header.
   *  - If exists -> send code immediately, go to code step.
   *  - If not -> show name fields, second click will actually send code.
   */
  const handleCheckEmail = async () => {
    setErrorMsg("");
    setInfoMsg("");

    const cleanEmail = (email || "").trim();

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setErrorMsg("Enter a valid school email address.");
      return;
    }

    if (!collegeId) {
      setErrorMsg("Please select your school before logging in.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${SERVER_BASE_URL}/post-login`, 
      {
        email: cleanEmail,
        collegeId,
        checkOnly: true,
      }, );

      const { exists, firstName: existingFirst, lastName: existingLast } =
        res.data || {};

      if (exists) {
        setIsExistingUser(true);

        // Optional: cache their name in state
        if (existingFirst) setFirstName(existingFirst);
        if (existingLast) setLastName(existingLast);

        await sendPasswordlessCode(cleanEmail);
        setStep("code");
        setInfoMsg("We sent a 6-digit code to your email/spam mail. It might take ~2mins, enter it above.");
      } else {
        setIsExistingUser(false);
        setInfoMsg(
          "Welcome to VibeU! Add your name so people know who they matched with (they only see your first name) then we’ll send your code."
        );
      }
    } catch (err) {
      console.error("Error checking email via /post-login:", err);
      const apiMsg =
        err?.response?.data?.message ||
        "Could not verify your email. Please try again.";
      setErrorMsg(apiMsg);
      setIsExistingUser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Second click for brand new users:
   *  - Requires first + last name.
   *  - Sends passwordless code via Auth0.
   */
  const handleSendCodeForNewUser = async () => {
    setErrorMsg("");
    setInfoMsg("");

    const cleanEmail = (email || "").trim();

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setErrorMsg("Enter a valid school email address.");
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg("Please enter your first and last name.");
      return;
    }

    try {
      setLoading(true);
      await sendPasswordlessCode(cleanEmail);
      setStep("code");
      setInfoMsg("We sent a 6-digit code to your email/spam mail. It might take ~2mins, enter it above.");
    } catch (err) {
      console.error("Error sending passwordless code:", err);
      const apiMsg =
        err?.response?.data?.error_description ||
        err?.response?.data?.message ||
        "Could not send code. Please check your email and try again.";
      setErrorMsg(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP and complete login as before.
   *  - Now /post-login returns { exists } but we don't strictly need it.
   */
  const handleVerifyCode = async () => {
    setErrorMsg("");
    setInfoMsg("");

    if (!code || code.trim().length < 4) {
      setErrorMsg("Enter the verification code from your email.");
      return;
    }

    try {
      setLoading(true);

      // 1) Exchange email + otp for tokens with Auth0
      const tokenResp = await axios.post(
        `https://${AUTH0_DOMAIN}/oauth/token`,
        {
          grant_type: "http://auth0.com/oauth/grant-type/passwordless/otp",
          client_id: AUTH0_CLIENT_ID,
          username: email,
          otp: code,
          realm: "email",
          audience: AUTH0_AUDIENCE,
          scope: "openid profile email",
        }
      );

      const { access_token, id_token } = tokenResp.data || {};

      if (!access_token || !id_token) {
        throw new Error("Missing tokens from Auth0 passwordless response.");
      }

      // 2) Decode id_token to get user info
      const payload = parseJwt(id_token) || {};
      const userEmail = payload.email || email;
      const fallbackName =
        payload.name ||
        payload.nickname ||
        (userEmail ? userEmail.split("@")[0] : "VibeU User");

      const typedName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const resolvedName = typedName || fallbackName;
      const emailVerified = payload.email_verified ?? true;

      const collegeIdForPost =
        localStorage.getItem("selectedSchoolId") || selectedCollege?.id;
      
      const postLoginBody = {
        email: userEmail,
        email_verified: emailVerified,
        collegeId: collegeIdForPost,
        checkOnly: false,
      };
      
      // Only send name when we *know* this is a new user
      if (isExistingUser === false) {
        postLoginBody.name = resolvedName;
      }

      // 3) Normal /post-login call with auth header (creates/updates user)
      await axios.post(
        `${SERVER_BASE_URL}/post-login`,
        postLoginBody,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );

      // 4) Persist tokens
      localStorage.setItem("vibeu_access_token", access_token);
      localStorage.setItem("vibeu_id_token", id_token);
      window.dispatchEvent(new Event("vibeu-auth-changed"));

      setInfoMsg("You’re in! Redirecting...");
      if (onSuccess) {
        onSuccess();
      } else {
        window.location.href = "/post-login";
      }
    } catch (err) {
      console.error("Error verifying passwordless code:", err);
      const apiMsg =
        err?.response?.data?.error_description ||
        err?.response?.data?.message ||
        "Invalid or expired code. Please request a new one.";
      setErrorMsg(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  // resend code on code step
  const handleResendCode = async () => {
    setErrorMsg("");
    setInfoMsg("");

    const cleanEmail = (email || "").trim();
    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setErrorMsg("Enter a valid school email address.");
      return;
    }

    try {
      setLoading(true);
      await sendPasswordlessCode(cleanEmail);
      setInfoMsg("We just sent you a new code.");
    } catch (err) {
      console.error("Error resending code:", err);
      const apiMsg =
        err?.response?.data?.error_description ||
        err?.response?.data?.message ||
        "Could not resend code. Please try again.";
      setErrorMsg(apiMsg);
    } finally {
      setLoading(false);
    }
  };

  const isEmailStep = step === "email";

  // primary button action for email step depends on existing vs new
  const primaryEmailAction =
    isExistingUser === false ? handleSendCodeForNewUser : handleCheckEmail;

  const emailButtonLabel = (() => {
    if (loading) {
      return isExistingUser === false ? "Sending code..." : "Checking...";
    }
    if (isExistingUser === false) return "Send code";
    return "Continue";
  })();

  return (
    <MotionBox
      sx={{
        mt: 1.5,
        width: "100%",
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
    >
      <MotionBox
        key={step}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
      >
        {isEmailStep && (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="School email"
              placeholder={exampleEmail}
              type="email"
              value={email}
              onChange={handleEmailChange}
              required
              autoComplete="email"
              inputMode="email"
              InputLabelProps={{ sx: commonLabelSx }}
              sx={{
                mb: isExistingUser === false ? 1.5 : 2,
                "& .MuiOutlinedInput-root": commonInputRootSx,
              }}
            />

            {/* Only show name fields if backend says user does NOT exist */}
            {isExistingUser === false && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.2,
                  mb: 1.5,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  InputLabelProps={{ sx: commonLabelSx }}
                  sx={{
                    "& .MuiOutlinedInput-root": commonInputRootSx,
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  InputLabelProps={{ sx: commonLabelSx }}
                  sx={{
                    "& .MuiOutlinedInput-root": commonInputRootSx,
                  }}
                />
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={primaryEmailAction}
              disabled={loading}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                py: 1.05,
                boxShadow: "0 12px 30px rgba(15,23,42,0.45)",
                transition:
                  "transform 0.18s cubic-bezier(0.175,0.885,0.32,1.275), box-shadow 0.18s ease-out",
                "&:hover": {
                  transform: loading ? "scale(1)" : "scale(1.02)",
                  boxShadow: "0 16px 40px rgba(15,23,42,0.6)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
            >
              {emailButtonLabel}
            </Button>
          </>
        )}

        {!isEmailStep && (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Verification code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              InputLabelProps={{ sx: commonLabelSx }}
              sx={{
                mb: 1.5,
                mt: 0.5,
                "& .MuiOutlinedInput-root": commonInputRootSx,
              }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyCode}
              disabled={loading}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                py: 1.05,
                boxShadow: "0 12px 30px rgba(15,23,42,0.45)",
                transition:
                  "transform 0.18s cubic-bezier(0.175,0.885,0.32,1.275), box-shadow 0.18s ease-out",
                "&:hover": {
                  transform: loading ? "scale(1)" : "scale(1.02)",
                  boxShadow: "0 16px 40px rgba(15,23,42,0.6)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
            >
              {loading ? "Verifying..." : "Verify & continue"}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={handleResendCode}
              disabled={loading}
              sx={{
                mt: 1,
                textTransform: "none",
                fontSize: "0.85rem",
                opacity: 0.9,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              Resend code
            </Button>
          </>
        )}
      </MotionBox>

      {infoMsg && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            textAlign: "center",
            color: "#9CA3AF",
          }}
        >
          {infoMsg}
        </Typography>
      )}

      {errorMsg && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            textAlign: "center",
            color: "#f97373",
          }}
        >
          {errorMsg}
        </Typography>
      )}
    </MotionBox>
  );
};

PasswordlessEmailLogin.propTypes = {
  selectedCollege: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    providerId: PropTypes.string,
    domains: PropTypes.arrayOf(PropTypes.string),
  }),
  onSuccess: PropTypes.func,
};

export default PasswordlessEmailLogin;
